import React, { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  addDoc,
  query,
  where,
  updateDoc,
  increment,
  serverTimestamp,
  deleteDoc,
} from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { db, auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardMedia,
  Typography,
  CircularProgress,
  Button,
  Menu,
  MenuItem,
  Avatar,
  IconButton,
  Divider
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import FavoriteIcon from '@mui/icons-material/Favorite';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ShareIcon from '@mui/icons-material/Share';
import { motion, AnimatePresence } from 'framer-motion';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BottomNavigationBar from './BottomNavigationBar';

const HomeScreen = () => {
  const [profiles, setProfiles] = useState([]);
  const [sponsoredPosts, setSponsoredPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userFirstName, setUserFirstName] = useState('');
  const [userProfilePhoto, setUserProfilePhoto] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [swipeDirection, setSwipeDirection] = useState({});
  const [userRole, setUserRole] = useState('user');

  // Track which profiles AND sponsored posts the current user has liked
  const [likedItems, setLikedItems] = useState(new Set()); // keys: "profile_{id}" or "post_{id}"

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const currentUserId = auth.currentUser?.uid;
        if (!currentUserId) return;

        // ── 1. Fetch user profiles ────────────────────────────────────────
        const profilesSnap = await getDocs(collection(db, 'users'));
        const fetchedProfiles = profilesSnap.docs
          .map((docSnap) => {
            const data = docSnap.data();
            return {
              id: docSnap.id,
              type: 'profile',
              firstName: data.firstName || 'Unknown',
              age: data.dob
                ? Math.floor((new Date() - new Date(data.dob)) / (1000 * 60 * 60 * 24 * 365))
                : 0,
              address: data.address || 'Somewhere',
              profilePhoto: data.profilePhoto || 'https://via.placeholder.com/300',
              bio: data.bio || 'No bio yet',
              interests: data.interests || [],
              likesCount: data.likesCount || 0,
              status: data.status || 'pending-payment',
              role: data.role || 'user',
            };
          })
          .filter((p) =>
            p.id !== currentUserId &&
            p.status === 'premium' &&
            p.email !== 'kmarieinc1998@gmail.com' &&
            p.role !== 'admin'
          );

        setProfiles(fetchedProfiles.reverse());

        // ── 2. Fetch sponsored posts ──────────────────────────────────────
        const postsSnap = await getDocs(collection(db, 'sponsoredPosts'));
        const activePosts = postsSnap.docs
          .map((docSnap) => ({
            id: docSnap.id,
            type: 'post',
            ...docSnap.data(),
            likesCount: docSnap.data().likesCount || 0,
          }))
          .filter((post) => post.active !== false);

        setSponsoredPosts(activePosts);

        // ── 3. Fetch all items the current user has liked ─────────────────
        const likesQuery = query(collection(db, `users/${currentUserId}/likes`));
        const likesSnap = await getDocs(likesQuery);

        const likedSet = new Set();
        likesSnap.forEach((likeDoc) => {
          const data = likeDoc.data();
          const key = `${data.type}_${data.targetId}`;
          likedSet.add(key);
        });

        setLikedItems(likedSet);
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    const fetchUserDetails = async () => {
      const user = auth.currentUser;
      if (!user) return;

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserFirstName(data.firstName || 'Guest');
          setUserProfilePhoto(data.profilePhoto || null);
        }
      } catch (err) {
        console.error('Error fetching user:', err);
      }
    };

    fetchData();
    fetchUserDetails();
  }, []);

  // ────────────────────────────────────────────────
  // Unified Like / Unlike Handler (for both profiles & posts)
  // ────────────────────────────────────────────────
  const handleLikeItem = async (itemId, itemType) => {
    const currentUserId = auth.currentUser?.uid;
    if (!currentUserId || !itemId) return;

    const key = `${itemType}_${itemId}`;
    const alreadyLiked = likedItems.has(key);

    // Optimistic UI update
    setLikedItems((prev) => {
      const newSet = new Set(prev);
      if (alreadyLiked) newSet.delete(key);
      else newSet.add(key);
      return newSet;
    });

    try {
      // 1. Update like count on the target item
      const targetRef = doc(db, itemType === 'profile' ? 'users' : 'sponsoredPosts', itemId);
      await updateDoc(targetRef, {
        likesCount: increment(alreadyLiked ? -1 : 1),
      });

      // 2. Record / remove like in current user's likes subcollection
      const likeRef = doc(db, `users/${currentUserId}/likes/${itemId}`);

      if (alreadyLiked) {
        await deleteDoc(likeRef);
      } else {
        await setDoc(likeRef, {
          targetId: itemId,
          type: itemType,
          createdAt: serverTimestamp(),
        });
      }

      // 3. Update local state to reflect new count
      const updateFn = (items) =>
        items.map((item) =>
          item.id === itemId
            ? { ...item, likesCount: (item.likesCount || 0) + (alreadyLiked ? -1 : 1) }
            : item
        );

      if (itemType === 'profile') {
        setProfiles(updateFn);
      } else {
        setSponsoredPosts(updateFn);
      }
    } catch (err) {
      console.error('Error updating like:', err);
      // Rollback optimistic update
      setLikedItems((prev) => {
        const newSet = new Set(prev);
        if (alreadyLiked) newSet.add(key);
        else newSet.delete(key);
        return newSet;
      });
    }
  };

  const handleSwipe = async (profileId, direction) => {
    const currentUserId = auth.currentUser?.uid;
    if (!currentUserId) return;

    setSwipeDirection((prev) => ({ ...prev, [profileId]: direction }));

    if (direction === 'right') {
      try {
        await addDoc(collection(db, 'matches'), {
          userId: currentUserId,
          targetUserId: profileId,
          action: 'like',
          timestamp: new Date(),
        });

        const mutualQuery = query(
          collection(db, 'matches'),
          where('userId', '==', profileId),
          where('targetUserId', '==', currentUserId),
          where('action', '==', 'like')
        );

        const mutualSnap = await getDocs(mutualQuery);
        if (!mutualSnap.empty) {
          const chatId = [currentUserId, profileId].sort().join('_');
          await setDoc(doc(db, 'chats', chatId), {
            participants: [currentUserId, profileId],
            lastMessage: 'You matched!',
            lastMessageTime: new Date(),
            unreadCount: 0,
          });
        }
      } catch (err) {
        console.error('Swipe error:', err);
      }
    }

    setTimeout(() => {
      setProfiles((prev) => prev.filter((p) => p.id !== profileId));
      setSwipeDirection((prev) => {
        const next = { ...prev };
        delete next[profileId];
        return next;
      });
    }, 450);
  };

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleSignOut = async () => {
    await signOut(auth);
    navigate('/');
  };

  const renderProfileCard = (profile, index) => {
    if (index > 2) return null;

    const scale = 1 - index * 0.04;
    const offset = index * 5;
    const isLiked = likedItems.has(`profile_${profile.id}`);

    return (
      <motion.div
        key={profile.id}
        style={{
          position: 'absolute',
          width: '85vw',
          maxWidth: 340,
          height: '65vh',
          maxHeight: 520,
          borderRadius: 24,
          overflow: 'hidden',
          boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
          zIndex: profiles.length - index,
        }}
        drag="x"
        dragConstraints={{ left: -300, right: 300 }}
        dragElastic={0.75}
        onDragEnd={(_, info) => {
          if (info.offset.x > 140) handleSwipe(profile.id, 'right');
          else if (info.offset.x < -140) handleSwipe(profile.id, 'left');
          else {
            setSwipeDirection((prev) => {
              const next = { ...prev };
              delete next[profile.id];
              return next;
            });
          }
        }}
        animate={{ x: 0, y: offset, scale, rotate: 0, opacity: 1 }}
        exit={{
          x: swipeDirection[profile.id] === 'right' ? 600 : -600,
          opacity: 0,
          rotate: swipeDirection[profile.id] === 'right' ? 45 : -45,
          transition: { duration: 0.42, ease: 'easeOut' },
        }}
        transition={{ type: 'spring', stiffness: 380, damping: 28 }}
      >
        <Card sx={{ height: '100%', position: 'relative' }}>
          <CardMedia
            component="img"
            image={profile.profilePhoto}
            alt={profile.firstName}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(to bottom, transparent 40%, #000000cc 75%)',
            }}
          />

          {swipeDirection[profile.id] === 'right' && <Box sx={likeOverlayStyle}>LIKE</Box>}
          {swipeDirection[profile.id] === 'left' && <Box sx={nopeOverlayStyle}>NOPE</Box>}

          <Box sx={{ position: 'absolute', bottom: 24, left: 20, right: 20, color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 0.5 }}>
              <Typography variant="h4" fontWeight={700}>
                {profile.firstName}
              </Typography>
              <Typography variant="h5">{profile.age}</Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <LocationOnIcon fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="body2">{profile.address}</Typography>
            </Box>

            {profile.bio && (
              <Typography variant="body2" sx={{ mb: 1.5, opacity: 0.9 }}>
                {profile.bio}
              </Typography>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {profile.interests.slice(0, 3).map((int) => (
                <Box
                  key={int}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.18)',
                    borderRadius: 12,
                    px: 1.5,
                    py: 0.5,
                  }}
                >
                  <Typography variant="caption">{int}</Typography>
                </Box>
              ))}
            </Box>

            {/* Like Button - Profile
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <IconButton
                onClick={() => handleLikeItem(profile.id, 'profile')}
                size="small"
                sx={{
                  color: isLiked ? '#C00068' : 'white',
                  bgcolor: 'rgba(255,255,255,0.15)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                }}
              >
                <FavoriteIcon
                  sx={{
                    fontSize: 28,
                    fill: isLiked ? '#C00068' : 'none',
                    stroke: 'white',
                    strokeWidth: 2,
                  }}
                />
              </IconButton>

              {profile.likesCount > 0 && (
                <Typography variant="body2" sx={{ ml: 1, fontWeight: 500 }}>
                  {profile.likesCount}
                </Typography>
              )}
            </Box> */}

            {/* Swipe Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 5, mt: 1 }}>
              <IconButton onClick={() => handleSwipe(profile.id, 'left')} sx={actionBtnStyle}>
                <CloseIcon fontSize="large" sx={{ color: '#ff3b30' }} />
              </IconButton>

              <IconButton onClick={() => handleSwipe(profile.id, 'right')} sx={actionBtnStyle}>
                <FavoriteIcon fontSize="large" sx={{ color: '#00cc66' }} />
              </IconButton>
            </Box>
          </Box>
        </Card>
      </motion.div>
    );
  };

  const renderSponsoredPost = (post) => {
    const isLiked = likedItems.has(`post_${post.id}`);

    return (
      <motion.div
        key={post.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Card
          sx={{
            mb: 4,
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            bgcolor: 'white',
            maxWidth: 500,
            mx: 'auto',
          }}
        >


          {/* Image */}
          <CardMedia
            component="img"
            image={post.imageUrl}
            alt={post.title}
            sx={{ width: '100%', height: { xs: 340, sm: 420 }, objectFit: 'cover' }}
          />

          {/* Like / Comment / Share */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, p: 2, borderBottom: '1px solid #eee' }}>
            {/* Real Like Button for Sponsored Post */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <IconButton
                onClick={() => handleLikeItem(post.id, 'post')}
                size="small"
                sx={{
                  color: isLiked ? '#C00068' : '#555',
                }}
              >
                <FavoriteIcon
                  sx={{
                    fontSize: 30,
                    fill: isLiked ? '#C00068' : 'none',
                    stroke: isLiked ? 'none' : '#555',
                    strokeWidth: 1.5,
                  }}
                />
              </IconButton>
              <Typography variant="body2" fontWeight={600}>
                {post.likesCount || 0}
              </Typography>
            </Box>


          </Box>

          {/* Caption */}
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              {post.title}
            </Typography>
            <Typography variant="body1" color="text.primary" sx={{ mb: 1 }}>
              {post.description}
            </Typography>
            {post.highlight && (
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ color: '#C00068', mt: 1.5, fontStyle: 'italic' }}
              >
                {post.highlight}
              </Typography>
            )}
          </Box>
        </Card>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={50} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f9f9f9', pb: { xs: '72px', md: 0 } }}>
      {/* Header */}
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight={700} color="#C00068">
          Discover
        </Typography>

        <Button
          onClick={handleMenuOpen}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: 'white',
            borderRadius: 20,
            px: 2,
            py: 0.5,
            border: '3px solid #C00068',
            textTransform: 'none',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          }}
        >
          <Avatar src={userProfilePhoto || '/default-avatar.jpg'} sx={{ width: 32, height: 32 }} />
          <Typography color="black" fontWeight={500}>
            {userFirstName}
          </Typography>
          <KeyboardArrowDownIcon fontSize="small" />
        </Button>

        <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleMenuClose}>
          <MenuItem onClick={() => { navigate('/profile'); handleMenuClose(); }}>
            My Profile
          </MenuItem>


          {userRole === 'admin' && (
            <MenuItem
              onClick={() => { navigate('/admin'); handleMenuClose(); }}
              sx={{ color: '#C00068', fontWeight: 'bold' }} // Making it stand out
            >
              <AdminPanelSettingsIcon sx={{ mr: 1, fontSize: 20 }} />
              Admin Portal
            </MenuItem>
          )}

          <MenuItem onClick={() => { navigate('/messages'); handleMenuClose(); }}>
            Messages
          </MenuItem>
          <MenuItem onClick={() => { navigate('/'); handleMenuClose(); }}>
            Homepage
          </MenuItem>
          <Divider /> {/* Optional: Add a divider before logout */}
          <MenuItem onClick={() => { handleSignOut(); handleMenuClose(); }}>
            Log out
          </MenuItem>
        </Menu>
      </Box>

      {/* Main content */}
      <Box
        sx={{
          position: 'relative',
          height: '78vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mt: 2,
        }}
      >
        {profiles.length > 0 ? (
          <AnimatePresence>
            {profiles.map((profile, i) => renderProfileCard(profile, i))}
          </AnimatePresence>
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '70vh',
              overflowY: 'auto',
              px: 2,
              pt: 3,
              pb: 6,
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '10px' },
              '&::-webkit-scrollbar-thumb': { background: '#C00068', borderRadius: '10px' },
              '&::-webkit-scrollbar-thumb:hover': { background: '#a80057' },
            }}
          >
            <Typography
              align="center"
              color="text.secondary"
              sx={{
                mb: 4,
                fontSize: '1rem',
                px: 2,
                opacity: 0.8
              }}
            >
              You’re all caught up.
              <br />
              Explore more while we find new matches.
            </Typography>



            {sponsoredPosts.length > 0 ? (
              sponsoredPosts.map(renderSponsoredPost)
            ) : (
              <Typography align="center" color="text.secondary" sx={{ mt: 4 }}>
                No sponsored content available at the moment.
              </Typography>
            )}

            <Box sx={{ height: '40px' }} />
          </Box>
        )}
      </Box>

      <BottomNavigationBar />
    </Box>
  );
};

// ── Reusable overlay styles ──────────────────────────────────────────────────
const likeOverlayStyle = {
  position: 'absolute',
  top: '15%',
  left: '10%',
  border: '5px solid #00cc66',
  borderRadius: 12,
  px: 4,
  py: 1,
  color: '#00cc66',
  fontSize: 48,
  fontWeight: 900,
  transform: 'rotate(-18deg)',
  pointerEvents: 'none',
};

const nopeOverlayStyle = {
  position: 'absolute',
  top: '15%',
  right: '10%',
  border: '5px solid #ff3b30',
  borderRadius: 12,
  px: 4,
  py: 1,
  color: '#ff3b30',
  fontSize: 48,
  fontWeight: 900,
  transform: 'rotate(18deg)',
  pointerEvents: 'none',
};

const actionBtnStyle = {
  bgcolor: 'white',
  width: 64,
  height: 64,
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  '&:hover': { bgcolor: '#f8f8f8' },
};

export default HomeScreen;